import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingContext } from '@/contexts/useOnboardingContext';
import { useAuth } from '@/contexts/useAuth';
import { updateProfile } from '@/lib/api/profile';
import { track } from '@/lib/analytics';
import type { FriendlyError } from '@/lib/errors';
import { ENGLISH_COMFORT_OPTIONS } from '@/types/profile';
import type { EnglishComfortLevel } from '@/types/profile';
import Button from '@/components/ui/Button';
import ErrorAlert from '@/components/ui/ErrorAlert';
import OnboardingShell from '@/components/layout/OnboardingShell';

export default function EnglishLevelPage() {
  const navigate = useNavigate();
  const { user, refreshProfile } = useAuth();
  const { state, setEnglishLevel, setStep } = useOnboardingContext();
  const [selected, setSelected] = useState<EnglishComfortLevel | undefined>(state.english_comfort_level);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);

  const handleContinue = async () => {
    if (!selected || !user?.id) return;
    setSaving(true);
    setError(null);
    const result = await updateProfile(user.id, {
      english_comfort_level: selected,
    });
    if (!result.success) {
      setError(result.error ?? null);
      setSaving(false);
      return;
    }
    setEnglishLevel(selected);
    setStep(4);
    await refreshProfile();
    track('onboarding_step_completed', { step: 'english_level' });
    navigate('/onboarding/concerns');
  };

  return (
    <OnboardingShell
      step={3}
      title={<>How comfortable are you with English?</>}
      subtitle={<>This helps us adjust how we guide you.</>}
    >
      <div className="space-y-3">
        {ENGLISH_COMFORT_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => setSelected(option.value)}
            className={`w-full text-left p-4 rounded-xl border-2 transition-colors flex items-center gap-3 cursor-pointer ${
              selected === option.value
                ? 'border-primary bg-primary-light'
                : 'border-border bg-card hover:border-primary/30'
            }`}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected === option.value ? 'border-primary' : 'border-border'}`}>
              {selected === option.value && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
            </div>
            <span className="text-xl">{option.icon}</span>
            <span className="font-sans font-medium text-text-primary">{option.label}</span>
          </button>
        ))}
      </div>

      {error && <ErrorAlert error={error} className="mt-6" />}

      <div className="mt-8">
        <Button
          fullWidth
          onClick={handleContinue}
          disabled={!selected || saving}
        >
          {saving ? 'Saving…' : 'Continue'}
        </Button>
      </div>
    </OnboardingShell>
  );
}
